import React, { memo, useMemo } from 'react';

import { MobileWizard, DesktopWizard } from './components';
import { useMobileState } from './hooks';

interface IWizardProps {
  sidebarTitle?: string;
  sidebarSubtitle?: string;
  steps: {
    title: string;
    content: React.ReactNode;
    isActive?: boolean;
    isCompleted?: boolean;
    fields?: {
      name: string;
      id: string;
      isRequired?: boolean;
      isCompleted?: boolean;
      isSkippable?: boolean;
      skippableTill?: number;
    }[];
    icon: React.ReactNode;
    absoluteStepIndex?: number;
  }[];
  currentStepIndex?: number;
  styleClass?: string;
  contentContainerStyleClass?: string;
  contentStyleClass?: string;
  disableNavigation?: boolean;
  showOnlineWarning?: boolean;
  onStepChange?: (stepIndex: number) => void;
}

const Wizard = ({
  steps,
  currentStepIndex = 0,
  sidebarTitle = '',
  sidebarSubtitle = '',
  styleClass = '',
  contentContainerStyleClass,
  contentStyleClass,
  disableNavigation,
  showOnlineWarning,
  onStepChange,
}: IWizardProps) => {
  const { isMobileScreen, isExpanded, handleToggleExpanded, handleCloseNavigation } = useMobileState({
    mobileScreenWidth: 767,
  });

  const Content = useMemo(() => {
    return steps.find((step: any) => step.absoluteStepIndex === currentStepIndex)?.content;
  }, [steps, currentStepIndex]);

  const currentStep = useMemo(() => {
    return steps.find((step: any) => step.absoluteStepIndex === currentStepIndex);
  }, [steps, currentStepIndex]);

  // Common props for both desktop and mobile wizards
  const commonWizardProps = useMemo(
    () => ({
      steps,
      currentStepIndex,
      disableNavigation,
      onStepChange,
      styleClass,
      contentContainerStyleClass,
      contentStyleClass,
      showOnlineWarning,
      Content,
    }),
    [
      steps,
      currentStepIndex,
      disableNavigation,
      onStepChange,
      styleClass,
      contentContainerStyleClass,
      contentStyleClass,
      showOnlineWarning,
      Content,
    ],
  );

  return isMobileScreen ? (
    <MobileWizard
      {...commonWizardProps}
      currentStep={currentStep}
      isExpanded={isExpanded}
      onToggleExpanded={handleToggleExpanded}
      onClose={handleCloseNavigation}
    />
  ) : (
    <DesktopWizard {...commonWizardProps} sidebarTitle={sidebarTitle} sidebarSubtitle={sidebarSubtitle} />
  );
};

export default memo(Wizard);
